<template>
  <div>
    <v-row>
      <v-col cols="3">
        <v-card>
          <v-toolbar color="primary" dark>
            <v-toolbar-title>System Messages</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="createNewMessage">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-toolbar>

          <v-list dense class="scrollable-list">
            <template v-if="messages.length > 0">
              <v-list-item
                v-for="(message, index) in messages"
                :key="index"
                @click="selectMessage(message)"
                :class="{ 'selected-item': selectedMessage === message }"
              >
                <v-list-item-content>
                  <v-list-item-title class="message-title" v-text="message.subject"></v-list-item-title>
                  <v-list-item-subtitle
                    class="message-date"
                    v-text="`${message.createdAt} (UTC)`"
                  ></v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-else>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="message-title">No messages</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="9">
        <v-card>
          <div>
            <v-card-title class="title-container">
              <div class="title-template">
                <span v-if="!selectedMessage.id">New System Message</span>
                <span v-else>{{ `${selectedMessage.createdAt} (UTC)` }}</span>
              </div>

              <div class="action-buttons">
                <v-btn icon @click="saveMessage" v-if="isNew" :disabled="!selectedMessage.content">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" v-bind="attrs" v-on="on">mdi-content-save</v-icon>
                    </template>
                    <span>Save this message to preview</span>
                  </v-tooltip>
                </v-btn>
                <v-btn icon @click="deleteMessage" v-if="!isNew">
                  <v-icon color="red">mdi-delete</v-icon>
                </v-btn>
              </div>
            </v-card-title>
          </div>

          <v-card-text>
            <div>
              <v-text-field
                :disabled="!isNew"
                v-model="selectedMessage.subject"
                placeholder="Subject"
                label="Subject"
                outlined
                dense
                hide-details
                class="mb-2"
              ></v-text-field>
            </div>
            <vue-editor
              v-model="selectedMessage.content"
              :editorToolbar="customToolbar"
              useCustomImageHandler
              :disabled="!isNew"
            ></vue-editor>

            <div v-if="previewContent" class="preview-container">
              <h3>Preview</h3>
              <div class="email-wrapper">
                <div v-html="previewContent" class="formatted-content"></div>
              </div>
            </div>
            <v-btn color="green" dark class="mt-4" :disabled="!isNew" :loading="isSending" @click="sendMessage"
              >Send</v-btn
            >
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor';
import systemMessageApi from '@/apis/systemMessage';

export default {
  components: { VueEditor },
  data() {
    return {
      messages: [],
      message: null,
      selectedMessage: {
        subject: '',
        date: new Date().toISOString().split('T')[0],
        content: '',
        id: 0,
      },
      previewContent: '',
      customToolbar: [
        [
          {
            header: [false, 1, 2, 3, 4, 5, 6],
          },
        ],
        ['bold', 'italic', 'underline', 'strike'],
        ['link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['code'],
      ],
      isEditingTitle: false,
      isSending: false,
    };
  },
  computed: {
    isNew() {
      return this.selectedMessage.id ? false : true;
    },
  },
  methods: {
    async init() {
      this.messages = await systemMessageApi.getSystemMessages();
      this.selectedMessage = {
        subject: '',
        date: new Date().toISOString().split('T')[0],
        content: '',
        id: 0,
      };
    },
    createNewMessage() {
      this.selectedMessage = {
        subject: '',
        date: new Date().toISOString().split('T')[0],
        content: '',
        id: 0,
      };
      this.previewContent = '';
    },
    selectMessage(message) {
      this.selectedMessage = message;
      this.previewContent = '';
    },
    saveMessage() {
      if (this.selectedMessage.content) {
        this.previewContent = this.selectedMessage.content;
      }
    },
    async deleteMessage() {
      if (!this.selectedMessage.id) return;
      const ok = await this.$myconfirm('Are you sure you want to delete this message?');
      if (!ok) return;
      this.isDeleting = true;
      try {
        const response = await systemMessageApi.deleteSystemMessageById(this.selectedMessage.id);
        if (response) {
          this.$myalert.success('Message deleted', true);
          this.init();
        }
      } catch (error) {
        this.$myalert.error(`Failed to delete message. ${error.message}`, true);
      } finally {
        this.isDeleting = false;
      }
    },
    async sendMessage() {
      this.isSending = true;
      try {
        const response = await systemMessageApi.sendSystemMessage(
          this.selectedMessage.subject,
          this.selectedMessage.content
        );
        if (response && response.id) {
          this.$myalert.success('Message sent', true);
          this.init();
        }
      } catch (error) {
        this.$myalert.error(`Message failed to send ${error.message}`, true);
      } finally {
        this.isSending = false;
        this.previewContent = '';
      }
    },
  },
  async mounted() {
    this.init();
  },
};
</script>

<style scoped>
.title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.title-template {
  flex-grow: 1;
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 1.25rem;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.v-list-item {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.v-list-item:hover {
  background-color: #e3f2fd;
}

.selected-item {
  background-color: #2196f3;
  color: white;
}

.message-title {
  font-size: 16px;
  font-weight: bold;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-date {
  font-size: 14px;
  color: #666;
}

.scrollable-list {
  max-height: calc(100vh - 84px - 94px + 12px);
  overflow-y: auto;
}

.email-wrapper {
  padding: 20px;
  font-family: Arial, sans-serif;
  border: 1px solid #ccc;
  border-radius: 5px;
  background: #f9f9f9;
}

.formatted-content {
  line-height: 1;
}

.formatted-content a {
  color: #007bff;
  text-decoration: underline;
}

.formatted-content pre {
  background-color: #f4f4f4;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
}

.preview-container {
  margin-top: 20px;
}
</style>
